// VideoVolume.tsx
import React, {useRef, useContext, MouseEvent, useEffect} from "react";
import Player from "video.js/dist/types/player";
import {isMobile} from "react-device-detect";

// Assets
import IconVolume from "../../../../../../Assets/SVGs/Icons/VideoPlayer/Volume";
import IconVolumeMuted from "../../../../../../Assets/SVGs/Icons/VideoPlayer/VolumeMuted";

// Import our new context
import {VideoPlayerSettingsContext} from "../../../../../../Context/VideoPlayerSettingsContext";

interface VideoVolumeProps {
    player: Player;
    isHovered: boolean;
    setIsHovered?: (isHovered: boolean) => void;
}

const VideoVolume: React.FC<VideoVolumeProps> = ({player, isHovered, setIsHovered}) => {
    const
        {volume, setVolume, muted, setMuted} = useContext(VideoPlayerSettingsContext),
        uiVolume = muted ? 0 : volume,

        trackRef = useRef<HTMLDivElement | null>(null),

        handlePointer = (hovered: boolean) => {
            setIsHovered?.(hovered);
        },

        updateVolumeFromPosition = (clientX: number) => {
            if (!trackRef.current) return;
            const rect = trackRef.current.getBoundingClientRect();
            let newVolume = (clientX - rect.left) / rect.width;
            newVolume = Math.max(0, Math.min(1, newVolume)); // Clamp between 0 and 1

            setVolume(newVolume);
            player.volume(newVolume);

            if (newVolume === 0) {
                setMuted(true);
                player.muted(true);
            } else {
                setMuted(false);
                player.muted(false);
            }
        },

        handleTrackClick = (e: MouseEvent<HTMLDivElement>) => {
            updateVolumeFromPosition(e.clientX);
        },

        handleThumbMouseDown = (e: MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            setIsHovered?.(true);

            const handleMouseMove = (e: MouseEvent) => {
                updateVolumeFromPosition(e.clientX);
                setIsHovered?.(true);
            };
            const handleMouseUp = () => {
                document.removeEventListener("mousemove", handleMouseMove as any);
                document.removeEventListener("mouseup", handleMouseUp);
                setIsHovered?.(false);
            };
            document.addEventListener("mousemove", handleMouseMove as any);
            document.addEventListener("mouseup", handleMouseUp);
        },

        toggleMute = () => {
            const nextMuted = !muted;

            setMuted(nextMuted);
            player.muted(nextMuted);
        };

    useEffect(() => {
        player.volume(volume);
        player.muted(muted);
    }, []);

    return (
        <div
            onPointerMove={() => handlePointer(true)}
            onPointerLeave={() => handlePointer(false)}
            style={{display: isMobile ? "none" : "flex", alignItems: "center"}}
            className={`video-controls__button video-controls__volume ${
                isHovered ? "is-hovered" : "is-not-hovered"
            }`}
        >
            <button
                onClick={toggleMute}
                style={{border: "none", background: "transparent", cursor: "pointer"}}
            >
                {muted ? IconVolumeMuted() : IconVolume()}
            </button>

            <div ref={trackRef} className="volume__track" onClick={handleTrackClick}>
                <div
                    className="volume__runner"
                    style={{width: `${uiVolume * 100}%`}}
                ></div>
                <div
                    className="volume__thumb"
                    style={{left: `${uiVolume * 100}%`}}
                    onMouseDown={handleThumbMouseDown}
                ></div>
            </div>
        </div>
    );
};

export default VideoVolume;
